/**
 * Type-safe query key factory for TanStack Query cache management.
 * Each domain has a root key and scoped sub-keys for granular cache invalidation.
 *
 * Usage:
 *   queryKeys.documents.all        → ['documents']
 *   queryKeys.documents.list()     → ['documents', 'list']
 *   queryKeys.documents.detail(id) → ['documents', 'detail', id]
 */
export const queryKeys = {
  /** Auth domain query keys */
  auth: {
    all: ['auth'] as const,
    status: () => [...queryKeys.auth.all, 'status'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  /** Documents domain query keys */
  documents: {
    all: ['documents'] as const,
    list: () => [...queryKeys.documents.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.documents.all, 'detail', id] as const,
    presignedUrl: (id: string) => [...queryKeys.documents.all, 'presignedUrl', id] as const,
  },

  /** Organizations domain query keys */
  organizations: {
    all: ['organizations'] as const,
    myMemberships: () => [...queryKeys.organizations.all, 'myMemberships'] as const,
    detail: (orgId: string) => [...queryKeys.organizations.all, 'detail', orgId] as const,
    members: (orgId: string) => [...queryKeys.organizations.all, 'members', orgId] as const,
  },

  /** User sessions domain query keys (HZVT-149) */
  sessions: {
    all: ['sessions'] as const,
    list: () => [...queryKeys.sessions.all, 'list'] as const,
  },

  /** Conversations domain query keys */
  conversations: {
    all: ['conversations'] as const,
    detail: (id: string) => [...queryKeys.conversations.all, 'detail', id] as const,
    messages: (conversationId: string) =>
      [...queryKeys.conversations.all, 'messages', conversationId] as const,
  },
} as const;
